<script module lang="ts">
  import type { Snippet } from "svelte";

  export interface BlacklistProps {
    blok: any;
    children?: Snippet;
  }
</script>

<script lang="ts">
  import { storyblokEditable } from "@storyblok/svelte";
  import { Section } from "@agapi/ui/section";
  import RichText from "../rich-text/rich-text.svelte";
  import { X } from "phosphor-svelte";
  import { css } from "@agapi/ui/panda/css";
  import { fade } from 'svelte/transition';
  import * as m from '$lib/parachute/messages';


  const { blok, ...props }: BlacklistProps = $props();

  // Container that wraps all blacklist items
  const containerStyle = css({
    display: "flex",
    flexWrap: "wrap",
    gap: "4",
    marginTop: "4"
  });

  // Each blacklist item is a flex row with the X icon and label in bold
  const itemStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "3",
    padding: "3",
    border: "feedback.error.border",
    backgroundColor: "feedback.error.background",
    borderRadius: "3"
  });

  const labelStyle = css({
    fontWeight: "bold",
    color: 'feedback.error.foreground'
  });

  const headingStyle = css({
    textStyle: "h3",
    fontWeight: "bold"
  });

  const iconStyles =  css({
    color: "error.600",
    cursor: "pointer"
  });

  const successItemStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "3",
    padding: "3",
    border: "feedback.success.border",
    backgroundColor: "feedback.success.background",
    borderRadius: "3",
    transition: "background-color 0.2s, border-color 0.2s"
  });

  const successLabelStyle = css({
    fontWeight: "bold",
    color: "feedback.success.foreground",
    textAlign: "center",
    transition: "color 0.5s",
    display: "block",
    width: "100%"
  });

  // Added interface for blacklist item
  interface BlacklistItem {
    _uid: string;
    label: string;
    removing: boolean;
    message?: string;
    width?: number;
    [key: string]: any;
  }


  // Initialize local reactive state for blacklist items with removal flag
  let items: BlacklistItem[] = $state(blok.blacklist ? blok.blacklist.map((item: any) => ({ ...item, removing: false })) : []);
  const removalMessages = m.blacklistMessages().split(',');
  function removeItem(item: BlacklistItem, event: MouseEvent): void {
    const container = (event.currentTarget as HTMLElement).parentElement;
    if (container) {
      item.width = container.offsetWidth;
    }
    // Filter messages based on the length of the item's label plus 3
    const availableMessages = removalMessages.filter((msg: string) => msg.length <= item.label.length + 3);
    item.message = availableMessages[Math.floor(Math.random() * availableMessages.length)] || "Bye!"; // fallback if no messages match
    item.removing = true;
    items = [...items]; // trigger reactivity for UI update
    setTimeout(() => {
      items = items.filter((i: BlacklistItem) => i._uid !== item._uid);
    }, 1500);
  }
</script>

<Section use={[[storyblokEditable, blok]]} {...props}>
  {#if blok.heading}
    <h2 class={headingStyle}>{blok.heading}</h2>
  {/if}

  {#if blok.description}
    <RichText blok={{ body: blok.description }} />
  {/if}

  <div class={containerStyle}>
    {#each items as item (item._uid)}
      <div style="width: {item.width ? item.width + 'px' : 'auto'}" class={item.removing ? successItemStyle : itemStyle} transition:fade>
        {#if !item.removing}
          <X size={20} weight="bold" class={iconStyles} onclick={(event) => removeItem(item, event)}>
            <title>{m.iconRemove()}</title>
          </X>
          <span class={labelStyle}>{item.label}</span>
        {:else}
          <span class={successLabelStyle}>{item.message}</span>
        {/if}
      </div>
    {/each}
  </div>
</Section>